'use strict';

/**
 * T315 Module Lock — LD-3 passphrase guardrails.
 *
 * A PIN remains usable (NOT hard-blocked, per the user); strength is
 * ENCOURAGED. We ship a small inline zxcvbn-style heuristic estimator
 * rather than pulling a ~400KB dependency into the bundle (plan: zxcvbn
 * inline-heuristic). Two research-mandated guardrails:
 *   - warn (not block) if the lock secret equals the account password
 *   - per-module random salt is handled by buildPinWrapEnvelope (pin-client)
 *
 * Pure + browser-safe.
 */

// Score 0..4 (zxcvbn convention): 0 too-guessable, 4 very-unguessable.
function estimateStrength(secret) {
  if (typeof secret !== 'string' || secret.length === 0) {
    return { score: 0, length: 0, warnings: ['empty'] };
  }
  const len = secret.length;
  const classes =
    (/[a-z]/.test(secret) ? 1 : 0) +
    (/[A-Z]/.test(secret) ? 1 : 0) +
    (/[0-9]/.test(secret) ? 1 : 0) +
    (/[^a-zA-Z0-9]/.test(secret) ? 1 : 0);
  const warnings = [];

  // sequences / repeats reduce effective entropy
  const allSame = /^(.)\1+$/.test(secret);
  const numericOnly = /^[0-9]+$/.test(secret);
  const common = ['1234', '0000', '1111', 'password', 'qwerty', 'letmein', '123456', 'abcd'];
  const isCommon = common.indexOf(secret.toLowerCase()) !== -1;

  let score;
  if (allSame || isCommon || len < 4) {
    score = 0;
  } else if (numericOnly && len <= 4) {
    score = 0;
    warnings.push('A 4-digit code is easy to guess — consider a longer phrase.');
  } else if (numericOnly && len <= 6) {
    score = 1;
  } else if (len < 8) {
    score = 1;
  } else if (len < 12) {
    score = classes >= 3 ? 3 : 2;
  } else if (len < 16) {
    score = classes >= 2 ? 3 : 2;
  } else {
    score = 4;
  }
  if (numericOnly && score > 1) score = Math.min(score, 2);

  return { score, length: len, classes, warnings };
}

// LD-3 — never hard-block in V1; only the format rules in pin-client apply.
function isAllowed(secret) {
  return typeof secret === 'string' && secret.length >= 4 && secret.length <= 64;
}

function checkAgainstAccountPassword(secret, accountPassword) {
  const equal = typeof secret === 'string' && typeof accountPassword === 'string' && secret === accountPassword;
  return { warn: equal, message: equal ? require('./policy').ML_STRINGS.WARN_EQUALS_PASSWORD : null };
}

module.exports = {
  estimateStrength,
  isAllowed,
  checkAgainstAccountPassword,
};
